import { useState } from "react";
import { PDFDocument, rgb, degrees, StandardFonts } from "pdf-lib";
import { renderPdfThumbnails, downloadBlob } from "../Helper/pdfThumpnails";
import Text from "./Text";
import Button from "./Buttons/Button";

const PREVIEW_WIDTH_PX = 260;

function hexToRgbValues(hex) {
    const clean = hex.replace("#", "");
    const bigint = parseInt(clean, 16);
    return {
        r: ((bigint >> 16) & 255) / 255,
        g: ((bigint >> 8) & 255) / 255,
        b: (bigint & 255) / 255,
    };
}

// Berechnet den Anker-Punkt so, dass der Text nach der Rotation visuell zentriert ist
// (pdf-lib rotiert um den Anker-Punkt, nicht um die Mitte des Textes)
function getCenteredRotatedTextPosition(pageWidth, pageHeight, textWidth, fontSize, rotationDeg) {
    const theta = (rotationDeg * Math.PI) / 180;
    const dx = textWidth / 2;
    const dy = fontSize / 2;
    const rotatedDx = dx * Math.cos(theta) - dy * Math.sin(theta);
    const rotatedDy = dx * Math.sin(theta) + dy * Math.cos(theta);
    return {
        x: pageWidth / 2 - rotatedDx,
        y: pageHeight / 2 - rotatedDy,
    };
}

export default function PDFWatermark() {
    const [fileName, setFileName] = useState("");
    const [pdfBytes, setPdfBytes] = useState(null);
    const [pageSize, setPageSize] = useState(null);
    const [previewThumb, setPreviewThumb] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [mode, setMode] = useState("text");

    // Text-Wasserzeichen
    const [text, setText] = useState("VERTRAULICH");
    const [fontSize, setFontSize] = useState(48);
    const [color, setColor] = useState("#FF0000");
    const [opacity, setOpacity] = useState(0.4);
    const [rotation, setRotation] = useState(45);

    // Bild-Wasserzeichen
    const [watermarkImageFile, setWatermarkImageFile] = useState(null);
    const [watermarkImagePreviewUrl, setWatermarkImagePreviewUrl] = useState(null);
    const [imageOpacity, setImageOpacity] = useState(0.4);
    const [imageScalePercent, setImageScalePercent] = useState(40);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setError("");
        setLoading(true);
        try {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            const pdfDoc = await PDFDocument.load(bytes);
            const firstPage = pdfDoc.getPages()[0];
            const { width, height } = firstPage.getSize();

            const thumbs = await renderPdfThumbnails(file, 0.6, 1);

            setFileName(file.name);
            setPdfBytes(bytes);
            setPageSize({ width, height });
            setPreviewThumb(thumbs[0]);
        } catch (err) {
            console.error(err);
            setError("PDF konnte nicht gelesen werden.");
        } finally {
            setLoading(false);
        }
    };

    const handleWatermarkImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setWatermarkImageFile(file);
        setWatermarkImagePreviewUrl(URL.createObjectURL(file));
    };

    const applyWatermark = async () => {
        if (!pdfBytes) return;
        if (mode === "text" && !text.trim()) {
            setError("Bitte einen Wasserzeichen-Text eingeben.");
            return;
        }
        if (mode === "image" && !watermarkImageFile) {
            setError("Bitte ein Bild für das Wasserzeichen auswählen.");
            return;
        }

        setSaving(true);
        setError("");
        try {
            const pdfDoc = await PDFDocument.load(pdfBytes);
            const pages = pdfDoc.getPages();

            if (mode === "text") {
                const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
                const { r, g, b } = hexToRgbValues(color);
                const textWidth = font.widthOfTextAtSize(text, fontSize);

                pages.forEach((page) => {
                    const { width, height } = page.getSize();
                    const { x, y } = getCenteredRotatedTextPosition(width, height, textWidth, fontSize, rotation);
                    page.drawText(text, {
                        x,
                        y,
                        size: fontSize,
                        font,
                        color: rgb(r, g, b),
                        opacity,
                        rotate: degrees(rotation),
                    });
                });
            } else {
                const imgBytes = await watermarkImageFile.arrayBuffer();
                const embeddedImage =
                    watermarkImageFile.type === "image/png"
                        ? await pdfDoc.embedPng(imgBytes)
                        : await pdfDoc.embedJpg(imgBytes);

                pages.forEach((page) => {
                    const { width, height } = page.getSize();
                    const imgWidth = width * (imageScalePercent / 100);
                    const imgHeight = imgWidth * (embeddedImage.height / embeddedImage.width);
                    page.drawImage(embeddedImage, {
                        x: (width - imgWidth) / 2,
                        y: (height - imgHeight) / 2,
                        width: imgWidth,
                        height: imgHeight,
                        opacity: imageOpacity,
                    });
                });
            }

            const outBytes = await pdfDoc.save();
            downloadBlob(
                outBytes,
                fileName.replace(/\.pdf$/i, "") + "-wasserzeichen.pdf",
                "application/pdf"
            );
        } catch (err) {
            console.error(err);
            setError("Fehler beim Hinzufügen des Wasserzeichens.");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setFileName("");
        setPdfBytes(null);
        setPageSize(null);
        setPreviewThumb(null);
        setError("");
    };

    const previewHeightPx = pageSize ? PREVIEW_WIDTH_PX * (pageSize.height / pageSize.width) : null;
    const previewFontSizePx = pageSize ? fontSize * (PREVIEW_WIDTH_PX / pageSize.width) : fontSize;

    return (
        <div className="flex flex-col gap-4">
            <Text type="body">
                PDF auswählen, um auf allen Seiten ein Text- oder Bild-Wasserzeichen einzufügen.
            </Text>

            {!fileName && (
                <input type="file" accept="application/pdf" onChange={handleFileChange} />
            )}

            {loading && <Text type="body">Wird geladen...</Text>}

            {fileName && !loading && (
                <div className="flex items-center justify-between">
                    <Text type="body" className="text-gray-500">{fileName}</Text>
                    <Button buttonColor="bg-gray-200" onClickAction={reset}>
                        Andere Datei wählen
                    </Button>
                </div>
            )}

            {pdfBytes && !loading && (
                <div className="flex flex-col md:flex-row gap-6">
                    {/* Einstellungen */}
                    <div className="flex flex-col gap-4 flex-1">
                        <div className="flex gap-4 text-sm">
                            <label className="flex items-center gap-1">
                                <input type="radio" checked={mode === "text"} onChange={() => setMode("text")} />
                                Text
                            </label>
                            <label className="flex items-center gap-1">
                                <input type="radio" checked={mode === "image"} onChange={() => setMode("image")} />
                                Bild
                            </label>
                        </div>

                        {mode === "text" && (
                            <>
                                <label className="flex flex-col gap-1 text-sm">
                                    Text
                                    <input
                                        className="border rounded-md p-2"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                    />
                                </label>

                                <label className="flex flex-col gap-1 text-sm">
                                    Schriftgrösse: {fontSize}pt
                                    <input
                                        type="range"
                                        min="12"
                                        max="120"
                                        value={fontSize}
                                        onChange={(e) => setFontSize(Number(e.target.value))}
                                    />
                                </label>

                                <label className="flex items-center gap-2 text-sm">
                                    Farbe
                                    <input
                                        type="color"
                                        value={color}
                                        onChange={(e) => setColor(e.target.value)}
                                    />
                                </label>

                                <label className="flex flex-col gap-1 text-sm">
                                    Transparenz: {Math.round(opacity * 100)}%
                                    <input
                                        type="range"
                                        min="0.05"
                                        max="1"
                                        step="0.05"
                                        value={opacity}
                                        onChange={(e) => setOpacity(Number(e.target.value))}
                                    />
                                </label>

                                <label className="flex flex-col gap-1 text-sm">
                                    Rotation: {rotation}°
                                    <input
                                        type="range"
                                        min="-90"
                                        max="90"
                                        value={rotation}
                                        onChange={(e) => setRotation(Number(e.target.value))}
                                    />
                                </label>
                            </>
                        )}

                        {mode === "image" && (
                            <>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    onChange={handleWatermarkImageChange}
                                />

                                <label className="flex flex-col gap-1 text-sm">
                                    Grösse: {imageScalePercent}% der Seitenbreite
                                    <input
                                        type="range"
                                        min="5"
                                        max="100"
                                        value={imageScalePercent}
                                        onChange={(e) => setImageScalePercent(Number(e.target.value))}
                                    />
                                </label>

                                <label className="flex flex-col gap-1 text-sm">
                                    Transparenz: {Math.round(imageOpacity * 100)}%
                                    <input
                                        type="range"
                                        min="0.05"
                                        max="1"
                                        step="0.05"
                                        value={imageOpacity}
                                        onChange={(e) => setImageOpacity(Number(e.target.value))}
                                    />
                                </label>
                            </>
                        )}
                    </div>

                    {/* Vorschau */}
                    {previewThumb && pageSize && (
                        <div className="flex flex-col items-center gap-2">
                            <Text type="body" className="text-gray-500 text-xs">Vorschau (Seite 1)</Text>
                            <div
                                style={{
                                    position: "relative",
                                    width: PREVIEW_WIDTH_PX,
                                    height: previewHeightPx,
                                    overflow: "hidden",
                                }}
                                className="border rounded-md shadow-sm"
                            >
                                <img
                                    src={previewThumb}
                                    alt="Seitenvorschau"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />

                                {mode === "text" && text && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: "50%",
                                            top: "50%",
                                            transform: `translate(-50%, -50%) rotate(${-rotation}deg)`,
                                            color,
                                            opacity,
                                            fontSize: previewFontSizePx,
                                            fontWeight: "bold",
                                            whiteSpace: "nowrap",
                                            fontFamily: "Arial, Helvetica, sans-serif",
                                        }}
                                    >
                                        {text}
                                    </div>
                                )}

                                {mode === "image" && watermarkImagePreviewUrl && (
                                    <img
                                        src={watermarkImagePreviewUrl}
                                        alt="Wasserzeichen-Vorschau"
                                        style={{
                                            position: "absolute",
                                            left: "50%",
                                            top: "50%",
                                            transform: "translate(-50%, -50%)",
                                            width: `${imageScalePercent}%`,
                                            opacity: imageOpacity,
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && <p style={{ color: "red" }}>{error}</p>}

            {pdfBytes && (
                <Button
                    buttonColor={saving ? "bg-gray-400" : "bg-green-100"}
                    onClickAction={applyWatermark}
                    disabled={saving}
                >
                    {saving ? "Wird erstellt..." : "Wasserzeichen einfügen & herunterladen"}
                </Button>
            )}
        </div>
    );
}